/** @jsxImportSource @emotion/react */
import { useMemo, useState } from 'react'
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom'
import { pick } from 'lodash'
import Button from 'react-bootstrap/Button'
import { Formik, Form, Field } from 'formik'
import Toast from 'components/atoms/Toast'
import Select from 'react-select'

import { confirm } from 'components/molecules/Confirmation'
import { useImpersonation } from 'hooks/useImpersonation'
import { useLabPermission } from 'hooks/useLabPermission'
import { UserPermission } from 'components/lab/user/permissions'
import ReadOnlyFieldset from 'components/atoms/ReadOnlyFieldset'
import {
  useDeleteUserMutation,
  useSendResetPasswordEmailMutation,
  useCreatePhysicianGroupUserMutation,
  useUpdateLabUserMutation,
} from 'store/services/users'
import * as Yup from 'yup'
import { useGetPhysicianGroupsQuery } from 'store/services/physician'
import DashboardContent from '../atoms/DashboardContent'
import { useGetLabUserQuery } from 'store/services/users'
import routes from '../constants/routes'
import { skipToken } from '@reduxjs/toolkit/query/react'
import { NotificationPreferences } from 'components/organisms/user/NotificationPreferences'
import { notificationTypes } from 'components/organisms/user/notificationsData'

const notificationKeys = [
  notificationTypes.HST_ORDER_CREATED,
  notificationTypes.ORDER_CANCELLED,
  notificationTypes.NEW_REPORT_READY_COX,
  notificationTypes.NEW_REPORT_READY_HST,
  notificationTypes.NEW_REPORT_READY_POX,
]

interface FormValues {
  firstName: ''
  lastName: ''
  email: ''
  physicianGroup: ''
  HST_ORDER_CREATED: boolean
  ORDER_CANCELLED: boolean
  NEW_REPORT_READY_COX: boolean
  NEW_REPORT_READY_HST: boolean
  NEW_REPORT_READY_POX: boolean
}

const initialValues = {
  firstName: '',
  lastName: '',
  email: '',
  physicianGroup: '',
  HST_ORDER_CREATED: false,
  ORDER_CANCELLED: false,
  NEW_REPORT_READY_COX: false,
  NEW_REPORT_READY_HST: false,
  NEW_REPORT_READY_POX: false,
}

const validation = Yup.object().shape({
  firstName: Yup.string().required('Required'),
  lastName: Yup.string().required('Required'),
  physicianGroup: Yup.number().required('Required'),
  email: Yup.string().required('Required').email('Invalid email'),
})

const PhysicianGroupUserUpsert = () => {
  const navigate = useNavigate()
  // TES-2881: without EDIT_PHYSICIANS, render the user read-only instead of editable.
  const readOnly = !useLabPermission(UserPermission.EDIT_PHYSICIANS)
  const { impersonatePhysicianGroup } = useImpersonation()
  const { userId } = useParams()
  const [searchParams] = useSearchParams()
  const [physicianGroupsSearch, setPhysicianGroupsSearch] = useState('')
  const [createPhysicianGroupUser] = useCreatePhysicianGroupUserMutation()
  const { data: physicianGroups } = useGetPhysicianGroupsQuery({
    search: physicianGroupsSearch || undefined,
  })
  const [updateUser] = useUpdateLabUserMutation()
  const [deleteUser] = useDeleteUserMutation()
  const [sendResetPasswordEmail] = useSendResetPasswordEmailMutation()
  const [physicianGroup, setPhysicianGroup] = useState<any>(searchParams.get('physicianGroup'))

  const physicianGroupOptions = physicianGroups?.map((physicianGroup) => ({
    label: physicianGroup.groupName,
    value: physicianGroup.id,
  }))

  const { data: user } = useGetLabUserQuery(userId ? { id: Number(userId) } : skipToken)

  const formInit = useMemo(
    () => ({
      ...initialValues,
      physicianGroup,
      ...(user && {
        ...pick(user, ['email', 'firstName', 'lastName']),
        physicianGroup: user?.physicianGroup,
        ...notificationKeys.reduce((acc, key) => ({
          ...acc,
          [key]: user?.notificationPreferences?.[key]?.email || false,
        }), {}),
      }),
    }),
    [user, physicianGroup],
  )

  return (
    <div>
      <DashboardContent
        icon="fas fa-people"
        title={userId ? 'Edit User' : 'Create User'}
        subtitle={
          <>
            <Link
              to={`${routes.index}${routes.physicianGroupUsers.root}${routes.physicianGroupUsers.directory}`}>
              {'< Back to User List'}
            </Link>
            <div css={{ position: 'absolute', top: '15px', right: '15px', display: 'flex' }}>
              {userId && (
                <>
                  <Button
                    variant="primary"
                    css={{ marginRight: 10 }}
                    onClick={() => impersonatePhysicianGroup(Number(userId))}>
                    <i className="fas fa-external-link-alt mr-2" />
                    Impersonate
                  </Button>
                  {!readOnly && (
                    <Button
                      variant="outline-danger"
                      css={{ marginRight: 10 }}
                      onClick={() =>
                        confirm('user')
                          .then(async () => {
                            await deleteUser({ id: Number(userId) }).unwrap()
                            navigate(
                              `${routes.index}${routes.physicianGroupUsers.root}${routes.physicianGroupUsers.directory}`,
                            )
                          })
                          .catch(() => {})
                      }>
                      Delete User
                    </Button>
                  )}
                  {!readOnly && !user?.isSsoEnabled && (
                    <Button
                      variant="outline-danger"
                      css={{}}
                      onClick={() => {
                        confirm('', {
                          title: 'Are you sure?',
                          description: 'This will send a password reset email to this user.',
                          yesAction: 'Yes, proceed',
                          noAction: 'Cancel',
                        })
                          .then(async () => {
                            try {
                              await sendResetPasswordEmail({ email: user?.email }).unwrap()
                              Toast({ type: 'success', label: `Password reset email sent.` })
                            } catch (err) {
                              Toast({ type: 'error', label: `Error sending password reset email.` })
                            }
                          })
                          .catch(() => {})
                      }}>
                      Reset Password
                    </Button>
                  )}
                </>
              )}
            </div>
          </>
        }
        breadcrumb={[
          {
            label: 'Users',
            to: `${routes.index}${routes.physicianGroupUsers.root}${routes.physicianGroupUsers.directory}`,
          },
          { label: userId ? 'Edit' : 'New' },
        ]}
        content={
          <Formik
            enableReinitialize
            validationSchema={validation}
            initialValues={formInit}
            onSubmit={async (values) => {
              const notificationPreferences = notificationKeys.reduce((acc, key) => ({
                ...acc,
                [key]: {
                  email: values[key],
                  sms: user?.notificationPreferences?.[key]?.sms || false,
                },
              }), {})
              const common = {
                physicianGroup: values.physicianGroup,
                firstName: values.firstName,
                lastName: values.lastName,
                email: values.email,
                notificationPreferences,
              }

              try {
                if (userId) {
                  await updateUser({
                    id: Number(userId),
                    ...common,
                  }).unwrap()
                  Toast({ type: 'success', label: `User updated!` })
                } else {
                  await createPhysicianGroupUser({
                    ...common,
                  }).unwrap()
                  Toast({ type: 'success', label: `User created!` })
                }
                navigate(
                  `${routes.index}${routes.physicianGroupUsers.root}${routes.physicianGroupUsers.directory}`,
                )
              } catch (err) {
                Toast({ type: 'error', label: err.data.message })
              }
            }}>
            {({
              values,
              errors,
              touched,
              handleChange,
              handleSubmit,
              setFieldValue,
              setValues,
            }) => (
              <Form id="userUpsert">
                <ReadOnlyFieldset readOnly={readOnly}>
                <div className="card-body row">
                  <div className="col-md-9">
                    <div className="card card-primary p-2 mb-0">
                      <div
                        className="card-header d-flex"
                        css={{
                          color: 'black !important',
                          backgroundColor: 'transparent !important',
                          justifyContent: 'space-between',
                          '&::after': {
                            display: 'none',
                          },
                        }}>
                        <h3 className="card-title">PHYSICIAN GROUP USER INFORMATION</h3>
                      </div>
                      <div className={`container bg-white mb-3 px-1 p-0`}>
                        <div className={`container pt-3 pb-2`}>
                          <div className="row mb-3">
                            <div className="col-6">
                              <label className="text-gray">
                                Physician Group <span className="text-danger">*</span>
                              </label>
                              <Select
                                isClearable
                                onInputChange={(v) => v && setPhysicianGroupsSearch(v)}
                                name="physicianGroup"
                                value={
                                  physicianGroupOptions?.find(
                                    (c) => c?.value == values.physicianGroup,
                                  ) ?? null
                                }
                                options={physicianGroupOptions}
                                onChange={(event) => {
                                  const val = event?.value
                                  setPhysicianGroup(val)
                                  setFieldValue('physicianGroup', val)
                                }}
                              />
                              {touched.physicianGroup && errors.physicianGroup && (
                                <div className="text-danger">Required</div>
                              )}
                            </div>
                          </div>
                          <div className="row mb-1">
                            <div className="col-6 form-group textGray">
                              <label htmlFor="firstName">
                                First Name
                                <span className="text-danger">*</span>
                              </label>
                              <Field
                                type="text"
                                name="firstName"
                                className="form-control"
                                placeholder="First Name"
                                value={values.firstName}
                                onChange={handleChange}
                                autoComplete="one-time-code"
                              />
                              {touched.firstName && errors.firstName && (
                                <div className="text-danger">{errors.firstName}</div>
                              )}
                            </div>
                            <div className="col-6 form-group textGray">
                              <label htmlFor="lastName">
                                Last Name
                                <span className="text-danger">*</span>
                              </label>
                              <Field
                                type="text"
                                name="lastName"
                                className="form-control"
                                placeholder="Last Name"
                                value={values.lastName}
                                onChange={handleChange}
                                autoComplete="one-time-code"
                              />
                              {touched.lastName && errors.lastName && (
                                <div className="text-danger">{errors.lastName}</div>
                              )}
                            </div>
                          </div>
                          <div className="row mb-3">
                            <div className="col-6 form-group textGray">
                              <label htmlFor="email">Email</label>
                              <span className="text-danger">*</span>
                              <Field
                                type="text"
                                name="email"
                                className="form-control"
                                placeholder="Email"
                                value={values.email}
                                onChange={(e) => {
                                  e.target.value = e.target.value.toLowerCase()
                                  handleChange(e)
                                }}
                              />
                              {touched.email && errors.email && (
                                <div className="text-danger">{errors.email}</div>
                              )}
                            </div>
                          </div>
                          <div className="row mt-4 pt-3">
                            <div className="col-12">
                              <div
                                className="card-header d-flex px-0"
                                css={{
                                  color: 'black !important',
                                  backgroundColor: 'transparent !important',
                                  justifyContent: 'space-between',
                                  borderBottom: '1px solid rgb(206, 212, 218)',
                                  '&::after': {
                                    display: 'none',
                                  },
                                }}>
                                <h3 className="card-title">EMAIL NOTIFICATIONS</h3>
                              </div>
                              <div
                                css={{
                                  paddingTop: '16px',
                                }}>
                                <NotificationPreferences view="physicianGroup" />
                              </div>
                            </div>
                          </div>
                        </div>
                        {!readOnly && (
                          <div className="col-sm-12 pt-3 pr-0 border-top">
                            <div className="col-sm-12 d-flex justify-content-end">
                              <Button type="submit" form="userUpsert" variant="primary">
                                Save Changes
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
                </ReadOnlyFieldset>
              </Form>
            )}
          </Formik>
        }
      />
    </div>
  )
}

export default PhysicianGroupUserUpsert
